import { css, cx } from "@emotion/css";
import { Button, Callout, ExternalLink, PopoverBasicHelp, tableBorder } from "@octopusdeploy/design-system-components";
import { DebouncedTextWithLabel as DebounceText, DeprecatedSelect, selectOptionsToItems } from "@octopusdeploy/design-system-octopus-components";
import { fontWeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ActionTemplateParameterResource, PlatformHubAccountResource, PropertyValueResource, ScopeValues, SensitiveValue, TemplateParameterResource, TemplateParameterValueResource } from "@octopusdeploy/octopus-server-client";
import { AccountType, bindParameterName, ControlType, isAccountControlType, isSensitiveValue, Permission, ProcessType } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import { isUndefined } from "lodash";
import * as React from "react";
import { useState, useCallback } from "react";
import { useScopeWidth } from "~/areas/platformHub/processEditor/useScopeWidth";
import TemplateParameterEditorDialog from "~/areas/platformHub/processTemplates/TemplateParameterEditorDialog";
import { useProducerTemplateProcessContext } from "~/areas/projects/components/Process/ProjectTemplates/useProducerTemplateProcessContext";
import ReadonlyInlineVariableValue from "~/areas/projects/components/Variables/TenantVariables/ReadonlyInlineVariableValue";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import type { ScopeSpecification } from "~/areas/variables/ReadonlyVariableResource/index";
import VariableScopeCell from "~/areas/variables/VariableScopeCell/index";
import CheckboxInput from "~/components/ControlInputs/CheckboxInput";
import MultiLineTextInput from "~/components/ControlInputs/MultiLineTextInput";
import SelectInput from "~/components/ControlInputs/SelectInput";
import SensitiveInput from "~/components/ControlInputs/SensitiveInput";
import SingleLineTextInput from "~/components/ControlInputs/SingleLineTextInput";
import { controlTypeValueToTextMap } from "~/components/ControlType/ControlTypeSelector";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import DeleteDialog from "~/components/Dialog/DeleteDialog";
import DialogOpener from "~/components/Dialog/DialogOpener";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import Markdown from "~/components/Markdown";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import type { PageContentCalloutType } from "~/components/PageContent/PageContent";
import { hasPermission } from "~/components/PermissionCheck/PermissionCheck";
import PopoverWhenFocused from "~/components/PopoverWhenFocused/index";
import convertPropertyValueResourceToString, { convertPropertyValueResourceToSensitiveValue } from "~/components/convertPropertyValueResourceToString";
import { Text } from "~/components/form/index";
import type { ActionCompleted } from "./ProcessTemplateWizardDialog";
interface TemplateParametersTableProps {
    parameters: TemplateParameterResource[];
    onChange: (parameters: ActionTemplateParameterResource[]) => void;
    availableScopeValues?: ScopeValues | undefined;
    platformHubAccounts: PlatformHubAccountResource[];
    templateLink: string | undefined;
    nameFilter?: string;
}
export function TemplateParametersTable({ parameters, onChange, availableScopeValues, platformHubAccounts, templateLink, nameFilter }: TemplateParametersTableProps) {
    const templateContext = useProducerTemplateProcessContext();
    const [parameterToEdit, setParameterToEdit] = React.useState<TemplateParameterResource | null>(null);
    const [parameterToEditIndex, setParameterToEditIndex] = React.useState<number | null>(null);
    const [parameterToDelete, setParameterToDelete] = React.useState<{
        parameter: TemplateParameterResource;
        index: number;
    } | null>(null);
    const [focusedValueKey, setFocusedValueKey] = useState<TemplateValueKey | null>(null);
    const [isEditingScope, setIsEditingScope] = useState(false);
    const { scopeHeaderRef, scopeWidth } = useScopeWidth();
    const setEditingParameter = (parameter: TemplateParameterResource | null) => {
        setParameterToEdit(parameter);
        setParameterToEditIndex(parameter ? parameters.indexOf(parameter) : null);
    };
    const onParameterChange = (newParameter: TemplateParameterResource, parameterIndex: number) => {
        onChange(parameters.map((p, i) => (i === parameterIndex ? newParameter : p)));
    };
    const deleteParameter = (parameterIndex: number) => {
        const parameter = parameters[parameterIndex];
        setParameterToDelete({ parameter, index: parameterIndex });
    };
    const confirmDeleteParameter = () => {
        if (parameterToDelete) {
            onChange(parameters.filter((_, i) => i !== parameterToDelete.index));
            setParameterToDelete(null);
        }
    };
    const deleteValue = (parameterIndex: number, valueIndex: number) => {
        onChange(parameters.map((p, i) => {
            if (i === parameterIndex) {
                return {
                    ...p,
                    Values: p.Values.filter((_, j) => j !== valueIndex),
                };
            }
            return p;
        }));
    };
    const parameterValueRows: ParameterValueRow[] = parameters.flatMap((p, parameterIndex): ParameterValueRow[] => {
        return p.Values.map<ParameterValueRow>((v, valueIndex) => ({ Parameter: p, Value: v, ParameterIndex: parameterIndex, ValueIndex: valueIndex })).concat([
            { Parameter: p, Value: null, ParameterIndex: parameterIndex, ValueIndex: p.Values.length },
        ]);
    });
    const isParameterValueRowDisplayed = (row: ParameterValueRow) => {
        const controlType = row.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
        // No parameter types should allow adding multiple values in project templates
        if (templateContext.type === ProcessType.ProjectTemplate && row.ValueIndex !== 0 && !row.Value) {
            return false;
        }
        // Hide the row with the "Add value" button for parameters that are not the account control type
        if (!isAccountControlType(controlType) && row.ValueIndex !== 0 && !row.Value) {
            return false;
        }
        if (nameFilter && !row.Parameter.Name.toLowerCase().includes(nameFilter.toLowerCase())) {
            return false;
        }
        return true;
    };
    return (<div>
            {parameterToEdit && (<DialogOpener open={true} onClose={() => setEditingParameter(null)}>
                    <TemplateParameterEditorDialog allParameters={parameters} parameter={parameterToEdit} onOk={(parameter) => {
                onChange(parameters.map((p, i) => (i === parameterToEditIndex ? parameter : p)));
                setEditingParameter(null);
            }} processTemplateLink={templateLink} close={() => setEditingParameter(null)}/>
                </DialogOpener>)}
            <DeleteDialog open={!!parameterToDelete} title={`Delete ${parameterToDelete?.parameter.Name ? `"${parameterToDelete.parameter.Name}"` : "Parameter"}?`} renderContent={() => (<Callout type={"warning"} title={"Potential data loss"}>
                        <div>
                            <p>If you delete a parameter, you'll lose any data associated with it in existing steps. You should ensure all usages are removed.</p>
                            {templateLink && (<p>
                                    We recommend deleting parameters via the <ExternalLink href={templateLink}>template's source code</ExternalLink>.
                                </p>)}
                        </div>
                    </Callout>)} onDeleteClick={async () => confirmDeleteParameter()} onClose={() => setParameterToDelete(null)}/>
            <GroupedDataTable data={parameterValueRows.filter(isParameterValueRowDisplayed)} columns={[
            {
                title: "Name",
                render: (data) => (data.ValueIndex === 0 ? <TemplateParameterNameCell data={data} onOpenEditParameterDialog={setEditingParameter} onDeleteParameter={deleteParameter}/> : null),
                width: "30%",
                isChild: (data) => data.ValueIndex !== 0,
                padding: space[12],
                collapseNullCellsUpwards: true,
                verticalAlign: "top",
            },
            {
                title: "Control Type",
                render: (data) => {
                    if (data.ValueIndex !== 0) {
                        return null;
                    }
                    const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
                    return <div>{controlTypeValueToTextMap.get(controlType) ?? controlType}</div>;
                },
                isChild: (data) => data.ValueIndex !== 0,
                padding: space[12],
                width: "15%",
                verticalAlign: "top",
            },
            {
                title: "Value",
                render: (data) => (<TemplateParameterValueCell parameter={data.Parameter} value={data.Value} onChange={(parameter) => onParameterChange(parameter, data.ParameterIndex)} valueIndex={data.ValueIndex} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} platformHubAccounts={platformHubAccounts}/>),
                padding: "0",
            },
            ...(availableScopeValues
                ? [
                    {
                        title: "Scope",
                        render: (data: ParameterValueRow) => (<TemplateParameterTableScopeCell data={data} onParameterChange={onParameterChange} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} isEditingScope={isEditingScope} setIsEditingScope={setIsEditingScope} availableScopeValues={availableScopeValues} scopeWidth={scopeWidth}/>),
                        padding: "0",
                        ref: scopeHeaderRef,
                    },
                ]
                : []),
            {
                title: "",
                render: (data: ParameterValueRow) => <TemplateParameterTableOverflowMenuCell data={data} deleteValue={deleteValue}/>,
                padding: "0",
                width: space[48],
                hideHeaderSeparator: true,
            },
        ]} getRowKey={(data) => `${data.ParameterIndex}-${data.ValueIndex}`}/>
        </div>);
}
export type ParameterValueRow = {
    Parameter: TemplateParameterResource;
    Value: TemplateParameterValueResource | null;
    ParameterIndex: number;
    ValueIndex: number;
};
interface TemplateParameterNameCellProps {
    data: ParameterValueRow;
    onOpenEditParameterDialog: (parameter: TemplateParameterResource) => void;
    onDeleteParameter: (parameterIndex: number) => void;
}
function TemplateParameterNameCell({ data, onOpenEditParameterDialog, onDeleteParameter }: TemplateParameterNameCellProps) {
    const [isOverflowMenuOpen, setIsOverflowMenuOpen] = React.useState(false);
    return (<div>
            <div className={styles.parameterNameContainer}>
                <div className={styles.parameterNameCellPrimaryHeader}>
                    <a href="" onClick={(e) => {
            e.preventDefault();
            onOpenEditParameterDialog(data.Parameter);
        }}>
                        <div>{data.Parameter.Name}</div>
                    </a>
                </div>
                <div className={cx("overflow-menu", { [styles.parameterNameCellOverflowMenuHidden]: !isOverflowMenuOpen, [styles.parameterNameCellOverflowMenuVisible]: isOverflowMenuOpen })}>
                    <OverflowMenu menuItems={[
            OverflowMenuItems.item("Edit parameter", () => onOpenEditParameterDialog(data.Parameter), { permission: Permission.PlatformHubEdit }),
            OverflowMenuItems.item("Delete parameter", () => onDeleteParameter(data.ParameterIndex), { permission: Permission.PlatformHubEdit }),
        ]} iconOrientation="horizontal" iconSize="small" onMenuStateChange={(menuState) => setIsOverflowMenuOpen(menuState.isOpen)}/>
                </div>
            </div>
            {data.Parameter.Label && <div className={styles.parameterNameCellSecondaryHeader}>{data.Parameter.Label}</div>}
            <div className={styles.parameterHelpText}>
                <Markdown markup={data.Parameter.HelpText} noMargins={true} mutedTextColor={true}/>
            </div>
        </div>);
}
interface TemplateParameterValueCellProps {
    parameter: TemplateParameterResource;
    value: TemplateParameterValueResource | null;
    onChange: (parameter: TemplateParameterResource) => void;
    platformHubAccounts: PlatformHubAccountResource[];
    focusedValueKey: TemplateValueKey | null;
    setFocusedValueKey: (key: TemplateValueKey | null) => void;
    valueIndex: number;
}
function isEmptySensitiveValue(newParameter: TemplateParameterResource) {
    return isSensitiveParameter(newParameter) && isSensitiveValue(newParameter.Values[0].Value) && isUndefined(newParameter.Values[0].Value.NewValue);
}
function TemplateParameterValueCell({ parameter, value, onChange, platformHubAccounts, focusedValueKey, setFocusedValueKey, valueIndex }: TemplateParameterValueCellProps) {
    const templateContext = useProducerTemplateProcessContext();
    const { doBusyTask } = useLegacyDoBusyTask();
    const onValueChange = (newParameter: TemplateParameterResource) => {
        if (isEmptySensitiveValue(newParameter)) {
            newParameter.Values = [];
        }
        onChange(newParameter);
    };
    const addValue = () => {
        onChange({ ...parameter, Values: [...parameter.Values, { Value: isSensitiveParameter(parameter) ? { HasValue: false, NewValue: undefined } : "", Scope: {} }] });
    };
    const valueKey = createTemplateValueKey(parameter.Name, valueIndex, "Value");
    const onFocus = useCallback(() => setFocusedValueKey(valueKey), [setFocusedValueKey, valueKey]);
    const onBlur = useCallback(() => setFocusedValueKey(null), [setFocusedValueKey]);
    const focused = focusedValueKey === valueKey;
    const controlType = parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
    if (!value) {
        const controlType = parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
        // Disable the "Add value" button for control types that are supplied by the project
        if (isControlTypeSuppliedByProject(controlType, templateContext.type)) {
            return <div className={cx(styles.disabledText, styles.valueContainer)}>Supplied by project</div>;
        }
        return (<div className={styles.valueContainer}>
                <Button label={valueIndex === 0 ? "Add value (optional)" : "Add another value"} onClick={() => addValue()} importance={"quiet"}/>
            </div>);
    }
    return (<div onFocus={onFocus} className={cx(styles.parameterValueAndScopeContainer, { [styles.parameterValueAndScopeTallerContainer]: showMultipleValuesAndScopesIndicators(templateContext.type, controlType, valueIndex, value) })}>
            {showMultipleValuesAndScopesIndicators(templateContext.type, controlType, valueIndex, value) && (<div className={styles.multipleValuesAndScopesIndicator}>
                    <div>Multiple values</div>
                </div>)}
            <div className={styles.valueContainer}>
                <PopoverWhenFocused isFocused={focused} onClickOutside={onBlur} position={{ top: 0, left: -12, right: 0 }} isContentCentered={true}>
                    {focused ? (<div className={styles.popoverContainer}>
                            <TemplateParameterValueInput parameter={parameter} accounts={platformHubAccounts} localNames={[]} doBusyTask={doBusyTask} value={value.Value} onChange={(value) => onValueChange({ ...parameter, Values: parameter.Values.map((v, i) => (i === valueIndex ? { ...v, Value: value ?? "" } : v)) })} type={parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText} autoFocus={true}/>
                        </div>) : (isSensitiveValue(value.Value) && value.Value.HasValue) || (!isSensitiveValue(value.Value) && value.Value) ? (<ReadonlyInlineVariableValue value={value.Value} variableType={getVariableTypeFromDisplaySettings(parameter.DisplaySettings["Octopus.ControlType"])} defaultValue={undefined} accounts={platformHubAccounts.reduce((acc, a) => ({ ...acc, [a.Id]: a.Name }), {})} certificates={{}} workerPools={{}} hasEditPermissions={hasPermission(Permission.PlatformHubEdit)} sourceItems={undefined}/>) : (<Text value="" placeholder={"Enter value"} onChange={noOp} hideUnderline={true} showValueAsTitleAttribute={true} monoSpacedFont={true}/>)}
                </PopoverWhenFocused>
                {isSensitiveParameter(parameter) && (<div className={styles.parameterValueHelp}>
                        <PopoverBasicHelp description={<p>Sensitive parameter values are stored securely in the database, while the parameter definition and reference to the value are stored in OCL.</p>} onOpen={onBlur} placement="left-start"/>
                    </div>)}
            </div>
        </div>);
}
interface TemplateParameterTableScopeCellProps {
    data: ParameterValueRow;
    onParameterChange: (newParameter: TemplateParameterResource, parameterIndex: number) => void;
    focusedValueKey: TemplateValueKey | null;
    setFocusedValueKey: (key: TemplateValueKey | null) => void;
    isEditingScope: boolean;
    setIsEditingScope: (isEditing: boolean) => void;
    availableScopeValues: ScopeValues;
    scopeWidth: number;
}
function TemplateParameterTableScopeCell({ data, onParameterChange, focusedValueKey, setFocusedValueKey, isEditingScope, setIsEditingScope, availableScopeValues, scopeWidth }: TemplateParameterTableScopeCellProps) {
    const templateContext = useProducerTemplateProcessContext();
    const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
    const getScopeCellContents = () => {
        if (isControlTypeWithConfigurableScope(controlType) || (data.Value?.Scope?.Environment && data.Value.Scope.Environment.length > 0)) {
            return data.Value ? (<TemplateParameterScopeCell parameter={data.Parameter} value={data.Value} onChange={(parameter) => onParameterChange(parameter, data.ParameterIndex)} index={data.ValueIndex} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} isEditing={isEditingScope} setIsEditing={setIsEditingScope} scopeValues={availableScopeValues} height={48} width={scopeWidth} isConsumer={false}/>) : null;
        }
        return data.ValueIndex === 0 ? <div className={cx(styles.disabledText, styles.noScopeRequiredContainer)}>No scope required</div> : null;
    };
    return (<div className={cx(styles.parameterValueAndScopeContainer, {
            [styles.parameterValueAndScopeTallerContainer]: showMultipleValuesAndScopesIndicators(templateContext.type, controlType, data.ValueIndex, data.Value),
        })}>
            {showMultipleValuesAndScopesIndicators(templateContext.type, controlType, data.ValueIndex, data.Value) && (<div className={styles.multipleValuesAndScopesIndicator}>
                    <div>Multiple scopes</div>
                </div>)}
            <div className={styles.scopeContainer}>{getScopeCellContents()}</div>
        </div>);
}
interface TemplateParameterTableOverflowMenuCellProps {
    data: ParameterValueRow;
    deleteValue: (parameterIndex: number, valueIndex: number) => void;
}
function TemplateParameterTableOverflowMenuCell({ data, deleteValue }: TemplateParameterTableOverflowMenuCellProps) {
    const templateContext = useProducerTemplateProcessContext();
    const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
    if (!(data.Value || isControlTypeSuppliedByProject(controlType, templateContext.type) || (!data.Value && data.ValueIndex === 0))) {
        return null;
    }
    return (<div className={cx(styles.parameterValueAndScopeContainer, {
            [styles.parameterValueAndScopeTallerContainer]: showMultipleValuesAndScopesIndicators(templateContext.type, controlType, data.ValueIndex, data.Value),
        })}>
            {showMultipleValuesAndScopesIndicators(templateContext.type, controlType, data.ValueIndex, data.Value) && <div className={styles.multipleValuesAndScopesIndicator}/>}
            <div className={styles.overflowMenuContainer}>
                <OverflowMenu menuItems={[
            data.Value
                ? OverflowMenuItems.item("Delete value", async () => deleteValue(data.ParameterIndex, data.ValueIndex), {
                    permission: Permission.PlatformHubEdit,
                })
                : null,
        ]}/>
            </div>
        </div>);
}
interface TemplateParameterValueInputProps {
    type: ControlType;
    value: PropertyValueResource;
    parameter: TemplateParameterResource;
    localNames: string[];
    doBusyTask: DoBusyTask;
    accounts: PlatformHubAccountResource[];
    onChange: (value: string | SensitiveValue | undefined) => void;
    autoFocus?: boolean;
}
function TemplateParameterValueInput({ doBusyTask, localNames, accounts, type, value, parameter, onChange, autoFocus }: TemplateParameterValueInputProps) {
    switch (type) {
        case ControlType.SingleLineText:
            return <SingleLineTextInput label={""} onChange={onChange} value={convertPropertyValueResourceToString(value)} localNames={localNames} warning={undefined} defaultValueIndicator={undefined} monoSpacedFont={true} autoFocus={autoFocus}/>;
        case ControlType.MultiLineText:
            return <MultiLineTextInput label={""} onChange={onChange} value={convertPropertyValueResourceToString(value)} localNames={localNames} warning={undefined} defaultValueIndicator={undefined} monoSpacedFont={true} autoFocus={autoFocus}/>;
        case ControlType.Select:
            const options = selectOptionsToItems(parameter.DisplaySettings["Octopus.SelectOptions"]);
            return (<SelectInput label={""} onChange={onChange} value={convertPropertyValueResourceToString(value)} resetValue={""} options={options} localNames={localNames} warning={undefined} allowClear={false} defaultValueIndicator={undefined} doBusyTask={doBusyTask}/>);
        case ControlType.Checkbox:
            return <CheckboxInput label={""} onChange={onChange} value={convertPropertyValueResourceToString(value)} resetValue={""} localNames={localNames} warning={undefined} defaultValueIndicator={undefined} doBusyTask={doBusyTask}/>;
        case ControlType.AmazonWebServicesAccount:
            return <PlatformHubAccountSelect onChange={onChange} value={convertPropertyValueResourceToString(value)} accounts={accounts} type={[AccountType.AmazonWebServicesAccount, AccountType.AmazonWebServicesOidcAccount]}/>;
        case ControlType.AzureAccount:
            return <PlatformHubAccountSelect onChange={onChange} value={convertPropertyValueResourceToString(value)} accounts={accounts} type={[AccountType.AzureServicePrincipal, AccountType.AzureSubscription, AccountType.AzureOidc]}/>;
        case ControlType.GoogleCloudAccount:
            return <PlatformHubAccountSelect onChange={onChange} value={convertPropertyValueResourceToString(value)} accounts={accounts} type={[AccountType.GoogleCloudAccount]}/>;
        case ControlType.UsernamePasswordAccount:
            return <PlatformHubAccountSelect onChange={onChange} value={convertPropertyValueResourceToString(value)} accounts={accounts} type={[AccountType.UsernamePassword]}/>;
        case ControlType.GenericOidcAccount:
            return <PlatformHubAccountSelect onChange={onChange} value={convertPropertyValueResourceToString(value)} accounts={accounts} type={[AccountType.GenericOidcAccount]}/>;
        case ControlType.Feed:
            throw new Error("Feed values are currently not supported in process templates");
        case ControlType.Sensitive:
            if (isFeatureToggleEnabled("process-template-sensitive-parameters")) {
                return <SensitiveInput value={convertPropertyValueResourceToSensitiveValue(value)} localNames={localNames} label={""} onChange={onChange} hideBindButton={true}/>;
            }
            throw new Error("Sensitive values are currently not supported in process templates");
        case ControlType.Certificate:
            throw new Error("Certificate values are currently not supported in process templates");
        case ControlType.WorkerPool:
            throw new Error("Worker pool values are currently not supported in process templates");
        case ControlType.Package:
            throw new Error("Package values are currently not supported in process templates");
        case ControlType.StepName:
            throw new Error("Step name values are currently not supported in process templates");
        case ControlType.TargetTags:
            throw new Error("Target tag values are currently not supported in process templates");
        case ControlType.Environments:
            throw new Error("Environment values are currently not supported in process templates");
        case ControlType.TenantTags:
            throw new Error("Tenant tag values are currently not supported in process templates");
        case ControlType.Teams:
            throw new Error("Teams values are currently not supported in process templates");
        case ControlType.Channels:
            throw new Error("Channel values are currently not supported in process templates");
        case ControlType.Project:
            throw new Error("Project values are currently not supported in process templates");
        default:
            return (<React.Fragment>
                    <DebounceText value={convertPropertyValueResourceToString(value)} warning={undefined} onChange={onChange}/>
                </React.Fragment>);
    }
}
interface PlatformHubAccountSelectProps {
    onChange: (value: string | undefined) => void;
    value: string | undefined;
    accounts: PlatformHubAccountResource[];
    type: AccountType[];
    placeholder?: string;
}
export function PlatformHubAccountSelect({ onChange, value, accounts, type, placeholder }: PlatformHubAccountSelectProps) {
    const accountItems = accounts.filter((a) => type.includes(a.AccountType)).map((a) => ({ value: a.Id, text: a.Name }));
    return (<div className={styles.accountSelectContainer}>
            <DeprecatedSelect onChange={onChange} value={value} items={accountItems} placeholder={placeholder}/>
        </div>);
}
interface TemplateParameterScopeCellProps {
    parameter: TemplateParameterResource;
    value: TemplateParameterValueResource;
    onChange: (parameter: TemplateParameterResource) => void;
    focusedValueKey: TemplateValueKey | null;
    setFocusedValueKey: (key: TemplateValueKey | null) => void;
    isEditing: boolean;
    setIsEditing?: (isEditing: boolean) => void;
    index: number;
    scopeValues: ScopeValues;
    height: number;
    width: number;
    isConsumer: boolean;
}
export function TemplateParameterScopeCell({ parameter, value, onChange, focusedValueKey, setFocusedValueKey, isEditing, setIsEditing, index, scopeValues, height, width, isConsumer }: TemplateParameterScopeCellProps) {
    const { doBusyTask } = useLegacyDoBusyTask();
    const onScopeChange = (newScope: ScopeSpecification, valueIndex: number) => {
        const newParameter = {
            ...parameter,
            Values: parameter.Values.map((v, i) => i === valueIndex
                ? {
                    ...v,
                    Scope: {
                        Environment: newScope.Environment ? [...newScope.Environment] : undefined,
                    },
                }
                : v),
        };
        onChange(newParameter);
    };
    const variableType = getVariableTypeFromDisplaySettings(parameter.DisplaySettings["Octopus.ControlType"]);
    const valueKey = createTemplateValueKey(parameter.Name, index, "Scope");
    const noScopes = Object.values(value.Scope ?? {}).reduce((acc, scope) => acc + (scope?.length ?? 0), 0) === 0;
    if (isConsumer && noScopes) {
        return null;
    }
    const overrideIsUnscoped = !!value.OverriddenScope?.Environment && value.OverriddenScope?.Environment?.length === 0;
    //If a consumer provides an unscoped variable value it takes precedence over a producer provided value whether scoped or unscoped.
    const excludedScopes = overrideIsUnscoped && isConsumer ? { Environment: scopeValues.Environments.map((e) => e.Id) } : value.OverriddenScope;
    return (<VariableScopeCell scope={value.Scope ?? {}} onScopeChanged={(scope) => onScopeChange(scope, index)} availableScopes={scopeValues} excludedScopes={excludedScopes} tagIndex={{}} environmentsById={{}} variableType={variableType} isEditing={valueKey === focusedValueKey && isEditing} isViewingAll={valueKey === focusedValueKey} doBusyTask={doBusyTask} onFocusEdit={() => {
            setFocusedValueKey(valueKey);
            setIsEditing && setIsEditing(true);
        }} onBlurEdit={() => {
            setFocusedValueKey(null);
            setIsEditing && setIsEditing(false);
        }} onFocusViewAll={() => setFocusedValueKey(valueKey)} onBlurViewAll={() => setFocusedValueKey(null)} onOpenEditorClicked={() => setIsEditing && setIsEditing(true)} cellHeight={height} containerWidth={width} isFullWidth={true} editorScope={"PlatformHub"} deleted={isConsumer} //if isConsumer display as readonly
     isEnvironmentExcludedDescription={value.OverrideValue ? `Overridden by ${bindParameterName(value.OverrideValue)}` : undefined}/>);
}
export type TemplateValueKey = `${string};${string};${string}`;
export function createTemplateValueKey(parameterName: string, index: number, type: "Value" | "Scope"): TemplateValueKey {
    return `${parameterName};${index};${type}`;
}
export function isSensitiveParameter(parameter: TemplateParameterResource): boolean {
    return parameter.DisplaySettings["Octopus.ControlType"] === ControlType.Sensitive;
}
const isControlTypeSuppliedByProject = (controlType: ControlType, processType: ProcessType.ProcessTemplate | ProcessType.ProjectTemplate): boolean => {
    return (controlType === ControlType.Package ||
        controlType === ControlType.Certificate ||
        controlType === ControlType.WorkerPool ||
        controlType === ControlType.StepName ||
        controlType === ControlType.TargetTags ||
        controlType === ControlType.Feed ||
        controlType === ControlType.Environments ||
        controlType === ControlType.TenantTags ||
        controlType === ControlType.Teams ||
        controlType === ControlType.Channels ||
        controlType === ControlType.Project ||
        (controlType === ControlType.Sensitive && (processType === ProcessType.ProjectTemplate || !isFeatureToggleEnabled("process-template-sensitive-parameters"))));
};
const isControlTypeWithConfigurableScope = (controlType: ControlType): boolean => {
    return isAccountControlType(controlType);
};
interface GetCalloutArgs {
    showInitialSuccessCallout: boolean;
    setShowInitialSuccessCallout: (value: boolean) => void;
    shouldShowShareAndPublishCallout: boolean;
    modelName: string;
    onPublishShareClick: () => void;
    onDismissShareAndPublishCallout: () => void;
}
export function GetCallout({ showInitialSuccessCallout, setShowInitialSuccessCallout, shouldShowShareAndPublishCallout, modelName, onPublishShareClick, onDismissShareAndPublishCallout }: GetCalloutArgs): PageContentCalloutType | undefined {
    if (showInitialSuccessCallout) {
        return {
            type: "custom",
            content: (<Callout type={"success"} title={`${modelName} was successfully committed, published and shared.`} canClose={true} onClose={() => setShowInitialSuccessCallout(false)}>
                    This template can now be used in projects.
                </Callout>),
        };
    }
    if (shouldShowShareAndPublishCallout) {
        return {
            type: "custom",
            content: <ShareAndPublishCallout onPublishShareClick={onPublishShareClick} onDismiss={onDismissShareAndPublishCallout} templateName={modelName}/>,
        };
    }
}
interface ShareAndPublishCalloutProps {
    templateName: string;
    onPublishShareClick: () => void;
    onDismiss: () => void;
}
function ShareAndPublishCallout({ templateName, onPublishShareClick, onDismiss }: ShareAndPublishCalloutProps) {
    return (<Callout type={"success"} title={`${templateName} was successfully committed.`} canClose={true} onClose={onDismiss}>
            To use this template in spaces, you'll need to{" "}
            <a href="#" onClick={(e) => {
            e.preventDefault();
            onPublishShareClick();
        }}>
                <strong>publish and share</strong>
            </a>{" "}
            this template.
        </Callout>);
}
export function createSnackbarMessage(actionsCompleted: ActionCompleted[], entityName: "process template" | "project template"): string {
    let snackbarMessage = "";
    if (actionsCompleted.length > 0) {
        snackbarMessage = `Successfully `;
        for (let i = 1; i <= actionsCompleted.length; i++) {
            snackbarMessage += `${actionsCompleted[i - 1]}`;
            if (actionsCompleted.length >= 2 && i < actionsCompleted.length) {
                if (i === actionsCompleted.length - 1) {
                    snackbarMessage += " and ";
                }
                else {
                    snackbarMessage += ", ";
                }
            }
        }
        snackbarMessage += ` the ${entityName}.`;
    }
    return snackbarMessage;
}
const showMultipleValuesAndScopesIndicators = (processType: ProcessType, controlType: ControlType, valueIndex: number, value: TemplateParameterValueResource | null): boolean => processType === ProcessType.ProcessTemplate && isAccountControlType(controlType) && valueIndex === 0 && !!value;
const baseCellHeightInRem = 3;
const multipleValuesAndScopesIndicatorHeightInRem = 1.75;
const styles = {
    parameterNameContainer: css({
        display: "flex",
        width: "100%",
        alignItems: "center",
        "&:hover": {
            ".overflow-menu": {
                visibility: "visible",
            },
        },
    }),
    parameterNameCellPrimaryHeader: css({
        font: text.code.regular.medium,
        fontWeight: fontWeight["700"],
        overflowWrap: "anywhere",
        hyphens: "auto",
        color: themeTokens.color.text.link.default,
    }),
    parameterNameCellOverflowMenuVisible: css({ visibility: "visible" }),
    parameterNameCellOverflowMenuHidden: css({ visibility: "hidden" }),
    parameterNameCellSecondaryHeader: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.tertiary,
        overflowWrap: "anywhere",
        hyphens: "auto",
    }),
    parameterValueAndScopeContainer: css({
        height: `${baseCellHeightInRem}rem`,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: "1",
    }),
    parameterValueAndScopeTallerContainer: css({
        height: `${baseCellHeightInRem + multipleValuesAndScopesIndicatorHeightInRem}rem`,
    }),
    parameterHelpText: css({
        font: text.body.regular.small,
        overflowWrap: "anywhere",
        hyphens: "auto",
    }),
    disabledText: css({
        color: themeTokens.color.text.disabled,
    }),
    noScopeRequiredContainer: css({
        marginLeft: "1.25rem",
        marginRight: "1.25rem",
    }),
    overflowMenuContainer: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: `${baseCellHeightInRem}rem`,
    }),
    valueContainer: css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: `${baseCellHeightInRem}rem`,
        paddingLeft: space[12],
        paddingRight: space[12],
    }),
    scopeContainer: css({
        height: `${baseCellHeightInRem}rem`,
        display: "flex",
        alignItems: "center",
    }),
    popoverContainer: css({
        paddingLeft: space[12],
        paddingRight: space[12],
        width: "100%",
    }),
    accountSelectContainer: css({
        height: "4rem",
    }),
    multipleValuesAndScopesIndicator: css({
        height: `${multipleValuesAndScopesIndicatorHeightInRem}rem`,
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.tertiary,
        borderBottom: tableBorder,
        display: "flex",
        alignItems: "center",
        paddingLeft: space[16],
        paddingRight: space[16],
    }),
    parameterValueHelp: css({
        alignContent: "center",
    }),
};
